import { createSignal, For, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import { ChefHat, Plus, Trash2, Utensils } from "@fleet/icons";

import { Button } from "@/components/ui/button";
import { TextField } from "@fleet/ui";
import { Select } from "@/components/ui/select";
import { addPantryItemMutation, deletePantryItemMutation } from "@mealplan/shared";
import { usePantryQuery, useRecipesByIngredientQuery } from "@mealplan/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";
import { OFFLINE_CACHE_STALE_TIME_MS, UNIT_OPTIONS } from "@/lib/constants";
import { registerUndo } from "@/lib/undo";
import { undoMessages } from "@/lib/undo-messages";
import { SwipeToDeleteRow } from "@/components/ui/swipe-to-delete-row";
import { EmptyShoppingIllustration } from "@/components/ui/illustrations";

interface PantryViewProps {
  /** Ingredient selected from the URL (`?ingredient=`). */
  selectedIngredient?: string;
  /** Reflects the selection to the URL (idempotent, `replace`). */
  onIngredientChange?: (ingredient: string) => void;
}

export function PantryView(props: PantryViewProps = {}) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();

  const pantry = usePantryQuery({
    db,
    drizzle,
    userId: session.userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const [name, setName] = createSignal("");
  const [qty, setQty] = createSignal("");
  const [unit, setUnit] = createSignal("");
  // Pantry → recipes using the ingredient interconnection: deep-linkable
  // (`?ingredient=`). The URL is the source of truth so the panel can be
  // copied and reopened.
  const selectedIngredient = (): string => props.selectedIngredient ?? "";
  const setSelectedIngredient = (value: string): void => props.onIngredientChange?.(value);

  const matchingRecipes = useRecipesByIngredientQuery({
    db,
    drizzle,
    userId: session.userId,
    ingredientName: selectedIngredient,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const handleAdd = async (e: Event): Promise<void> => {
    e.preventDefault();
    const dbInstance = drizzle();
    const uid = session.userId();
    const trimmed = name().trim();
    if (!dbInstance || !uid || !trimmed) {
      return;
    }
    const quantity = Number(qty().replace(",", "."));
    const newId = await addPantryItemMutation(dbInstance, uid, {
      name: trimmed,
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : null,
      unit: unit(),
    });
    registerUndo(undoMessages(t, t(msg`Added to pantry`)), () =>
      deletePantryItemMutation(dbInstance, newId),
    );
    setName("");
    setQty("");
    setUnit("");
  };

  return (
    <div class="space-y-6">
      <div>
        <h2 class="font-serif text-2xl font-semibold tracking-tight">{t(msg`Pantry`)}</h2>
        <p class="text-sm text-muted-foreground">
          {t(msg`What you already have at home — subtracted when generating the shopping list.`)}
        </p>
      </div>

      <form class="flex flex-wrap items-center gap-2" onSubmit={(e) => void handleAdd(e)}>
        <TextField
          class="w-20 shrink-0"
          placeholder={t(msg`Qty`)}
          value={qty()}
          onInput={(e) => setQty(e.currentTarget.value)}
          data-testid="pantry-qty"
        />
        <div class="w-24 shrink-0">
          <Select
            options={UNIT_OPTIONS}
            value={unit() || null}
            onChange={(value) => setUnit(value ?? "")}
            placeholder={t(msg`Unit`)}
            aria-label={t(msg`Unit`)}
            data-testid="pantry-unit"
          />
        </div>
        <TextField
          class="min-w-40 flex-1"
          placeholder={t(msg`e.g. Rice`)}
          value={name()}
          onInput={(e) => setName(e.currentTarget.value)}
          data-testid="pantry-name"
        />
        <Button
          type="submit"
          size="sm"
          class="shrink-0 gap-1"
          disabled={!drizzle() || !name().trim()}
          data-testid="pantry-add"
        >
          <Plus class="h-4 w-4" />
          {t(msg`Add`)}
        </Button>
      </form>

      <Show
        when={(pantry.data ?? []).length > 0}
        fallback={
          <Show when={!pantry.isLoading}>
            <div class="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border p-12 text-center">
              <EmptyShoppingIllustration class="mb-4 h-28 w-36 text-muted-foreground" />
              <h3 class="text-lg font-semibold">{t(msg`Empty pantry`)}</h3>
            </div>
          </Show>
        }
      >
        <ul class="space-y-1">
          <For each={pantry.data}>
            {(item) => {
              const handleDelete = (): void => {
                const dbInstance = drizzle();
                const uid = session.userId();
                if (!dbInstance || !uid) {
                  return;
                }
                void (async () => {
                  await deletePantryItemMutation(dbInstance, item.id);
                  registerUndo(undoMessages(t, t(msg`Removed from pantry`)), async () => {
                    await addPantryItemMutation(dbInstance, uid, {
                      name: item.name ?? "",
                      quantity: item.quantity ?? null,
                      unit: item.unit ?? null,
                    });
                  });
                })();
              };
              return (
                <li>
                  <SwipeToDeleteRow onDelete={handleDelete} data-testid="pantry-item">
                    <div class="group flex items-center gap-3 border border-border/40 bg-card/50 px-3 py-2">
                      <span class="flex-1 text-sm">
                        {item.name}
                        <Show when={item.quantity !== null && item.quantity !== undefined}>
                          <span class="text-muted-foreground">
                            {" "}
                            · {item.quantity} {item.unit ?? ""}
                          </span>
                        </Show>
                      </span>
                      <button
                        type="button"
                        class="rounded-md p-1 text-muted-foreground opacity-60 transition-opacity hover:bg-accent/10 hover:text-accent hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                        aria-label={t(msg`Find recipes using this ingredient`)}
                        onClick={() => setSelectedIngredient(item.name ?? "")}
                        data-testid="pantry-find-recipes"
                      >
                        <Utensils class="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        class="opacity-0 transition-opacity group-hover:opacity-60 hover:!opacity-100"
                        aria-label={t(msg`Delete item`)}
                        onClick={handleDelete}
                      >
                        <Trash2 class="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    </div>
                  </SwipeToDeleteRow>
                </li>
              );
            }}
          </For>
        </ul>
      </Show>

      {/* Interconexión: recetas que usan el ingrediente seleccionado. */}
      <Show when={selectedIngredient()}>
        <div
          class="space-y-3 rounded-xl border border-accent/25 bg-card/50 p-4"
          data-testid="pantry-recipe-matches"
        >
          <div class="flex items-center justify-between gap-2">
            <h3 class="flex items-center gap-2 text-sm font-semibold">
              <ChefHat class="h-4 w-4 text-accent" />
              {t(msg`Recipes with "{name}"`, { name: selectedIngredient() })}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedIngredient("")}
              aria-label={t(msg`Close`)}
            >
              {t(msg`Close`)}
            </Button>
          </div>
          <Show
            when={(matchingRecipes.data ?? []).length > 0}
            fallback={
              <p class="text-sm text-muted-foreground">
                {t(msg`No recipes use this ingredient yet.`)}
              </p>
            }
          >
            <ul class="space-y-1">
              <For each={matchingRecipes.data ?? []}>
                {(r) => (
                  <li>
                    <Link
                      to="/recipes/$recipeId"
                      params={{ recipeId: r.id }}
                      class="flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                    >
                      <span class="truncate">{r.name}</span>
                      <span class="text-xs text-muted-foreground">
                        {t(msg`{count} servings`, { count: r.servings ?? 0 })}
                      </span>
                    </Link>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </div>
      </Show>
    </div>
  );
}
